<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { cloneStore } from '../cloneStore.js';
  import { get } from 'svelte/store';
  import { authStore } from '../authStore';
  import { selectedCountry } from '../countryStore'; // <-- Import Country Store
  import StoreAutocomplete from '../components/StoreAutocomplete.svelte';
  import { authFetch } from '../authService'; // <-- IMPORT THIS

  let productName = '';
  let storeName = ''; 
  let originalPrice = '';
  let discountPrice = '';
  let description = '';
  let discountPercentage = '';
  let barcode = '';
  let imageBase64 = ''; // New variable to hold the image

  let currentUser = null;
  let error = '';
  let isSubmitting = false;

  // Subscribe to auth store to get the user ID for the upload record
  authStore.subscribe(value => {
    currentUser = value.user;
  });

  onMount(() => {
    // 1. Check if we have data passed from the Scan/Clone action
    const data = get(cloneStore);
    
    if (data) {
      console.log("Populating form from scan data:", data);
      productName = data.productName || '';
      storeName = data.storeName || ''; 
      originalPrice = data.originalPrice || '';
      discountPrice = data.discountPrice || '';
      description = data.description || '';
      barcode = data.barcode || '';
      // Retrieve the image passed from the Scan page
      imageBase64 = data.imageBase64 || ''; 
      
      // Trigger calculation immediately if data exists
      calculateDiscount();
      
      // Clear the store so we don't re-use this data accidentally
      cloneStore.set(null);
    }

    // 2. STORE PERSISTENCE LOGIC
    // If the store name wasn't found in the scan, try to pre-fill it 
    // with the store the user visited last time.
    if (!storeName) {
      const lastUsedStore = localStorage.getItem('lastUsedStoreName');
      if (lastUsedStore) {
        storeName = lastUsedStore;
      }
    }
  });

  // Reactive calculation: Updates percentage when prices or description change
  function calculateDiscount() {
    const orig = parseFloat(originalPrice);
    const disc = parseFloat(discountPrice);

    if (orig > 0 && disc >= 0 && orig > disc) {
      // Calculate percentage based on price difference
      discountPercentage = (((orig - disc) / orig) * 100).toFixed(2);
    } else if (description && description.toLowerCase().includes('buy 1 get 1 free')) {
      discountPercentage = "50.00";
    } else if (description && description.toLowerCase().includes('buy 2 get 1 free')) {
      discountPercentage = (1/3 * 100).toFixed(2);
    } else {
      // Don't clear it if the user manually entered a percentage, 
      // but do clear it if the inputs are invalid and no text match found.
      // (Optional: strictly clear it if you prefer auto-calc only)
      // discountPercentage = ''; 
    }
  }
  
  // Run calculation whenever these fields change
  $: if (originalPrice || discountPrice || description) {
    calculateDiscount();
  }

  async function handleSubmit() {
    if (!productName || !storeName) {
      error = "Product Name and Store Name are required.";
      return;
    }
    isSubmitting = true;
    error = '';

    // 3. SAVE TO LOCAL STORAGE
    // Save this store name so it's pre-filled for the next product scan
    if (storeName) {
      localStorage.setItem('lastUsedStoreName', storeName);
    }
    
    const payload = {
      productName,
      storeName,
      originalPrice: originalPrice ? parseFloat(originalPrice) : null,
      discountPrice: discountPrice ? parseFloat(discountPrice) : null,
      description,
      discountPercentage: discountPercentage ? parseFloat(discountPercentage) : null,
      barcode,
      uploadedByUserId: currentUser?.idTokenClaims?.oid,
      
      // --- NEW: Add Country Code ---
      countryCode: $selectedCountry,
      
      // Pass the image data to the backend
      imageBase64: imageBase64 
    };

    try {
      const response = await authFetch(getApiUrl('/products'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to save product discount.');
      }

      // Redirect back to the Scan page to allow rapid entry of the next item
      navigate('/products/scan'); 
      
    } catch (e) {
      error = e.message;
    } finally {
      isSubmitting = false;
    }
  }
</script>

<div class="form-container">
  <h2>Add Product Discount</h2>
  
  {#if imageBase64}
    <div class="image-preview">
      <img src={`data:image/jpeg;base64,${imageBase64}`} alt="Scanned Product" />
      <p class="image-note">This image will be uploaded with your product.</p>
    </div>
  {/if}

  <form on:submit|preventDefault={handleSubmit}>
    <fieldset>
      <div class="form-group">
        <label for="store-name">Store Name (Required)</label>
        <!-- Use the new Autocomplete component here -->
        <StoreAutocomplete 
            bind:value={storeName} 
            placeholder="Start typing to search stores..." 
            required={true} 
        />
      </div>

      <div class="form-group">
        <label for="product-name">Product Name (Required)</label>
        <input type="text" id="product-name" bind:value={productName} required />
      </div>

      <div class="form-group">
        <label for="barcode">Barcode (UPC/EAN)</label>
        <input type="text" id="barcode" bind:value={barcode} placeholder="Scanned barcode" />
      </div>

      <div class="price-row">
        <div class="form-group">
            <label for="orig-price">Original Price ($)</label>
            <input type="number" step="0.01" id="orig-price" bind:value={originalPrice} />
        </div>
        <div class="form-group">
            <label for="disc-price">Discount Price ($)</label>
            <input type="number" step="0.01" id="disc-price" bind:value={discountPrice} />
        </div>
      </div>

      <div class="form-group">
        <label for="description">Promotion Description</label>
        <input type="text" id="description" bind:value={description} placeholder="e.g., Buy 1 Get 1 Free" />
      </div>

      <div class="form-group">
        <label for="disc-percent">Discount Percentage (%)</label>
        <input type="number" step="0.01" id="disc-percent" bind:value={discountPercentage} placeholder="Calculated automatically" />
      </div>
    </fieldset>

    <button type="submit" disabled={isSubmitting}>
      {#if isSubmitting}Saving...{:else}Save Product Discount{/if}
    </button>
    {#if error} <p class="error">{error}</p> {/if}
  </form>
</div>

<style>
  .form-container { max-width: 700px; margin: 2rem auto; }
  fieldset { border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; margin-bottom: 1rem; }
  .form-group { margin-bottom: 1rem; text-align: left; }
  .price-row { display: flex; gap: 1rem; }
  .price-row .form-group { flex: 1; }
  
  label { display: block; margin-bottom: 0.5rem; font-weight: 500; }
  input { width: 100%; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; font-size: 1rem; box-sizing: border-box; }
  
  button { 
    padding: 0.75rem; 
    background-color: #007bff; 
    color: white; 
    border: none; 
    border-radius: 4px; 
    font-size: 1.1rem; 
    cursor: pointer; 
    width: 100%;
  }
  button:disabled { background-color: #aaa; }
  .error { color: red; text-align: center; margin-top: 1rem; }

  .image-preview {
    text-align: center;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #eee;
  }
  .image-preview img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  .image-note {
    font-size: 0.9rem;
    color: #6c757d;
    margin-top: 0.5rem;
  }
</style>